/**
 * User Detail Modal Component
 * View and manage user details
 */

import { LoadingSpinner } from '@components/ui';
import { apiUrl } from '@config/api';
import { useAuth } from '@contexts/AuthContext';
import { useUserActions } from '@hooks/useUserActions';
import { useUserDetail } from '@hooks/useUserDetail';
import type { QuotaOverrides } from '@parlay-picker/shared/subscriptionTier';
import {
  AlertTriangle,
  CheckCircle,
  Mail,
  RotateCcw,
  ShieldCheck,
  Trash2,
  X,
  XCircle,
} from 'lucide-react';
import { type ReactElement, useCallback, useEffect, useState } from 'react';

// =============================================================================
// Types
// =============================================================================

interface UserDetailModalProps {
  userId: string;
  onClose: () => void;
  onUpdate: () => void;
}

interface QuotaUsageData {
  modes: Array<{ mode: string; used: number; limit: number }>;
  analysis: { used: number; limit: number };
  tracking: { used: number; limit: number };
}

const QUOTA_MODES = ['standard', 'detailed', 'premium', 'analysis', 'tracking'] as const;
type QuotaMode = (typeof QUOTA_MODES)[number];

// =============================================================================
// Component
// =============================================================================

export function UserDetailModal({ userId, onClose, onUpdate }: UserDetailModalProps): ReactElement {
  const { authFetch } = useAuth();
  const { user, loading, error, refetch } = useUserDetail(userId);
  const {
    updateRole,
    updateEmailVerification,
    updateQuotaOverrides,
    deleteUser,
    loading: actionLoading,
  } = useUserActions();

  const [selectedRole, setSelectedRole] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  // Quota state
  const [quotaUsage, setQuotaUsage] = useState<QuotaUsageData | null>(null);
  const [quotaUsageLoading, setQuotaUsageLoading] = useState(false);
  const [quotaDraft, setQuotaDraft] = useState<Record<QuotaMode, string>>({
    standard: '',
    detailed: '',
    premium: '',
    analysis: '',
    tracking: '',
  });

  // Initialize selected role when user loads
  useEffect(() => {
    if (user && !selectedRole) {
      setSelectedRole(user.role);
    }
  }, [user, selectedRole]);

  // Initialize quota draft from user overrides
  useEffect(() => {
    if (user) {
      const overrides = user.quotaOverrides;
      setQuotaDraft({
        standard: overrides?.standard != null ? String(overrides.standard) : '',
        detailed: overrides?.detailed != null ? String(overrides.detailed) : '',
        premium: overrides?.premium != null ? String(overrides.premium) : '',
        analysis: overrides?.analysis != null ? String(overrides.analysis) : '',
        tracking: overrides?.tracking != null ? String(overrides.tracking) : '',
      });
    }
  }, [user]);

  // Fetch quota usage
  const fetchQuotaUsage = useCallback(async () => {
    if (!userId) return;
    setQuotaUsageLoading(true);
    try {
      const response = await authFetch(apiUrl(`/api/admin/users/${userId}/quota-usage`));
      if (response.ok) {
        setQuotaUsage(await response.json());
      }
    } catch {
      // Silently fail — usage display is informational
    } finally {
      setQuotaUsageLoading(false);
    }
  }, [authFetch, userId]);

  useEffect(() => {
    fetchQuotaUsage();
  }, [fetchQuotaUsage]);

  const handleRoleChange = async () => {
    if (!user || selectedRole === user.role) return;

    setActionError(null);
    setActionSuccess(null);

    const result = await updateRole(user.id, selectedRole);
    if (result.success) {
      setActionSuccess('Role updated successfully');
      onUpdate();
    } else {
      setActionError(result.error);
    }
  };

  const handleVerificationToggle = async () => {
    if (!user) return;

    setActionError(null);
    setActionSuccess(null);

    const result = await updateEmailVerification(user.id, !user.emailVerified);
    if (result.success) {
      setActionSuccess(`Email ${user.emailVerified ? 'unverified' : 'verified'} successfully`);
      onUpdate();
    } else {
      setActionError(result.error);
    }
  };

  const handleDelete = async () => {
    if (!user || deleteConfirmText !== 'DELETE') return;

    setActionError(null);
    setActionSuccess(null);

    const result = await deleteUser(user.id);
    if (result.success) {
      setActionSuccess('User deleted successfully');
      // Hide delete confirmation immediately on success to prevent flicker
      setShowDeleteConfirm(false);
      setDeleteConfirmText('');
      setTimeout(() => {
        onUpdate();
        onClose();
      }, 1000);
    } else {
      setActionError(result.error);
    }
  };

  const handleSaveOverrides = async () => {
    if (!user) return;

    setActionError(null);
    setActionSuccess(null);

    // Convert draft strings to QuotaOverrides
    const overrides: QuotaOverrides = {};
    let hasAnyOverride = false;

    for (const mode of QUOTA_MODES) {
      const val = quotaDraft[mode].trim();
      if (val === '') continue;
      const num = parseInt(val, 10);
      if (Number.isNaN(num) || num < 0) {
        setActionError(`${mode} must be a non-negative integer`);
        return;
      }
      overrides[mode] = num;
      hasAnyOverride = true;
    }

    const result = await updateQuotaOverrides(user.id, hasAnyOverride ? overrides : null);
    if (result.success) {
      setActionSuccess('Quota overrides saved');
      await Promise.all([refetch(), fetchQuotaUsage()]);
      onUpdate();
    } else {
      setActionError(result.error);
    }
  };

  const clearModeOverride = (mode: QuotaMode) => {
    setQuotaDraft((prev) => ({ ...prev, [mode]: '' }));
  };

  // Check if draft differs from current overrides
  const hasOverrideChanges = (): boolean => {
    if (!user) return false;
    const overrides = user.quotaOverrides;
    for (const mode of QUOTA_MODES) {
      const draftVal = quotaDraft[mode].trim();
      const currentVal = overrides?.[mode];
      if (draftVal === '' && currentVal == null) continue;
      if (draftVal === '' && currentVal != null) return true;
      if (draftVal !== '' && currentVal == null) return true;
      if (draftVal !== '' && currentVal != null && parseInt(draftVal, 10) !== currentVal)
        return true;
    }
    return false;
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRoleBadgeColor = (role: string): string => {
    switch (role) {
      case 'admin':
        return 'bg-red-500/20 text-red-400';
      case 'elite':
        return 'bg-indigo-500/20 text-indigo-400';
      case 'premium':
        return 'bg-purple-500/20 text-purple-400';
      case 'pro':
        return 'bg-blue-500/20 text-blue-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  // Get usage for a mode from quotaUsage data
  const getUsage = (mode: QuotaMode): { used: number; limit: number } | null => {
    if (!quotaUsage) return null;
    if (mode === 'analysis') return quotaUsage.analysis;
    if (mode === 'tracking') return quotaUsage.tracking;
    return quotaUsage.modes.find((m) => m.mode === mode) ?? null;
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-theme-surface rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-theme-border">
          <h2 className="text-xl font-bold text-theme-text">User Details</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-theme-text-secondary hover:text-theme-text hover:bg-theme-bg-secondary rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {loading && (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          )}

          {error && (
            <div className="p-4 bg-theme-danger/10 border border-theme-danger rounded-lg text-theme-danger">
              {error}
            </div>
          )}

          {actionError && (
            <div className="p-4 bg-theme-danger/10 border border-theme-danger rounded-lg text-theme-danger flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{actionError}</span>
            </div>
          )}

          {actionSuccess && (
            <div className="p-4 bg-theme-success/10 border border-theme-success rounded-lg text-theme-success flex items-start gap-2">
              <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{actionSuccess}</span>
            </div>
          )}

          {user && !loading && (
            <>
              {/* User Info */}
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-theme-text">{user.displayName}</h3>
                    <p className="text-sm text-theme-text-secondary">
                      {user.firstName} {user.lastName}
                    </p>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${getRoleBadgeColor(user.role)}`}
                  >
                    {user.role === 'admin' && <ShieldCheck className="w-4 h-4" />}
                    <span className="capitalize">{user.role}</span>
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-theme-text-secondary">Email</span>
                    <p className="text-theme-text">{user.email}</p>
                  </div>
                  <div>
                    <span className="text-sm text-theme-text-secondary">Email Status</span>
                    <p
                      className={`flex items-center gap-1 ${
                        user.emailVerified ? 'text-theme-success' : 'text-theme-text-tertiary'
                      }`}
                    >
                      {user.emailVerified ? (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          <span>Verified</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="w-4 h-4" />
                          <span>Unverified</span>
                        </>
                      )}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-theme-text-secondary">Joined</span>
                    <p className="text-theme-text">{formatDate(user.createdAt)}</p>
                  </div>
                  <div>
                    <span className="text-sm text-theme-text-secondary">Last Updated</span>
                    <p className="text-theme-text">{formatDate(user.updatedAt)}</p>
                  </div>
                  <div>
                    <span className="text-sm text-theme-text-secondary">Onboarding</span>
                    <p className="text-theme-text">{formatDate(user.onboardingCompletedAt)}</p>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="bg-theme-bg rounded-lg p-4">
                <h4 className="text-sm font-medium text-theme-text-secondary mb-3">
                  Activity Stats
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-2xl font-bold text-theme-text">{user.stats.parlays}</span>
                    <p className="text-sm text-theme-text-secondary">Parlays Tracked</p>
                  </div>
                  <div>
                    <span className="text-2xl font-bold text-theme-text">{user.stats.legs}</span>
                    <p className="text-sm text-theme-text-secondary">Legs Created</p>
                  </div>
                </div>
              </div>

              {/* Subscription */}
              {user.subscription && (
                <div className="bg-theme-bg rounded-lg p-4">
                  <h4 className="text-sm font-medium text-theme-text-secondary mb-3">
                    Subscription
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-theme-text-secondary">Status</span>
                      <span className="text-theme-text capitalize">{user.subscription.status}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-theme-text-secondary">Stripe Customer ID</span>
                      <span className="text-theme-text font-mono text-xs">
                        {user.subscription.stripeCustomerId}
                      </span>
                    </div>
                    {user.subscription.currentPeriodEnd && (
                      <div className="flex justify-between">
                        <span className="text-theme-text-secondary">Period End</span>
                        <span className="text-theme-text">
                          {formatDate(user.subscription.currentPeriodEnd)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Quota Management */}
              <div className="bg-theme-bg rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-theme-text-secondary">
                    Quota Management
                  </h4>
                  <button
                    type="button"
                    onClick={fetchQuotaUsage}
                    disabled={quotaUsageLoading}
                    className="p-1 text-theme-text-tertiary hover:text-theme-text transition-colors"
                    title="Refresh usage"
                  >
                    <RotateCcw className={`w-4 h-4 ${quotaUsageLoading ? 'animate-spin' : ''}`} />
                  </button>
                </div>

                {/* Usage Display */}
                {quotaUsage && (
                  <div className="space-y-1.5">
                    <p className="text-xs text-theme-text-tertiary uppercase tracking-wide">
                      Today&apos;s Usage
                    </p>
                    {QUOTA_MODES.map((mode) => {
                      const usage = getUsage(mode);
                      if (!usage) return null;
                      const remaining = Math.max(0, usage.limit - usage.used);
                      return (
                        <div key={mode} className="flex items-center justify-between text-sm">
                          <span className="text-theme-text capitalize">{mode}</span>
                          <span className="text-theme-text-secondary">
                            {usage.used}/{usage.limit}
                            <span className="text-theme-text-tertiary ml-1">
                              ({remaining} remaining)
                            </span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Override Inputs */}
                <div className="space-y-2 pt-2 border-t border-theme-border/50">
                  <p className="text-xs text-theme-text-tertiary uppercase tracking-wide">
                    Quota Overrides (empty = tier default)
                  </p>
                  {QUOTA_MODES.map((mode) => {
                    const tierDefault = user.tierDefaults[mode];
                    const hasOverride = quotaDraft[mode].trim() !== '';
                    return (
                      <div key={mode} className="flex items-center gap-2">
                        <span className="text-sm text-theme-text capitalize w-20">{mode}</span>
                        <input
                          type="number"
                          min={0}
                          value={quotaDraft[mode]}
                          onChange={(e) =>
                            setQuotaDraft((prev) => ({ ...prev, [mode]: e.target.value }))
                          }
                          placeholder=""
                          className="w-20 px-2 py-1 bg-theme-surface border border-theme-border rounded text-theme-text text-sm focus:outline-none focus:ring-1 focus:ring-theme-primary"
                          disabled={actionLoading}
                        />
                        <span className="text-xs text-theme-text-tertiary">
                          tier: {tierDefault}
                        </span>
                        {hasOverride && (
                          <button
                            type="button"
                            onClick={() => clearModeOverride(mode)}
                            className="p-0.5 text-theme-text-tertiary hover:text-theme-text transition-colors"
                            title={`Clear ${mode} override`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    onClick={handleSaveOverrides}
                    disabled={actionLoading || !hasOverrideChanges()}
                    className="mt-2 px-4 py-1.5 bg-theme-primary-fill text-theme-on-primary-fill text-sm rounded-lg hover:bg-theme-primary-fill-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Save Overrides
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-4 pt-4 border-t border-theme-border">
                <h4 className="text-sm font-medium text-theme-text-secondary">Admin Actions</h4>

                {/* Change Role */}
                <div className="flex items-center gap-3">
                  <select
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    className="flex-1 px-4 py-2 bg-theme-bg border border-theme-border rounded-lg text-theme-text focus:outline-none focus:ring-2 focus:ring-theme-primary"
                    disabled={actionLoading}
                  >
                    <option value="free">Free</option>
                    <option value="pro">Pro</option>
                    <option value="premium">Premium</option>
                    <option value="elite">Elite</option>
                    <option value="admin">Admin</option>
                  </select>
                  <button
                    type="button"
                    onClick={handleRoleChange}
                    disabled={actionLoading || selectedRole === user.role}
                    className="px-4 py-2 bg-theme-primary-fill text-theme-on-primary-fill rounded-lg hover:bg-theme-primary-fill-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Update Role
                  </button>
                </div>

                {/* Toggle Email Verification */}
                <button
                  type="button"
                  onClick={handleVerificationToggle}
                  disabled={actionLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-theme-bg border border-theme-border text-theme-text rounded-lg hover:bg-theme-bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  <span>Mark Email as {user.emailVerified ? 'Unverified' : 'Verified'}</span>
                </button>

                {/* Delete User */}
                {!showDeleteConfirm ? (
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={actionLoading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-theme-danger/10 border border-theme-danger text-theme-danger rounded-lg hover:bg-theme-danger/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete User</span>
                  </button>
                ) : (
                  <div className="space-y-2 p-4 bg-theme-danger/10 border border-theme-danger rounded-lg">
                    <p className="text-sm text-theme-danger font-medium">
                      This will permanently delete the user and all associated data.
                    </p>
                    <p className="text-sm text-theme-text-secondary">
                      Type <strong>DELETE</strong> to confirm:
                    </p>
                    <input
                      type="text"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="DELETE"
                      className="w-full px-4 py-2 bg-theme-bg border border-theme-border rounded-lg text-theme-text focus:outline-none focus:ring-2 focus:ring-theme-danger"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowDeleteConfirm(false);
                          setDeleteConfirmText('');
                        }}
                        className="flex-1 px-4 py-2 bg-theme-bg border border-theme-border text-theme-text rounded-lg hover:bg-theme-bg-secondary transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleDelete}
                        disabled={deleteConfirmText !== 'DELETE' || actionLoading}
                        className="flex-1 px-4 py-2 bg-theme-danger text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Delete User
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
